<template>
    <div>
      <transition name="fade" @before-enter="beforeEnter" @enter="enter" @leave="leave">
        <div v-if="displayedItems.length" class="grid grid-cols-1 sm:grid-cols-1 lg:grid-cols-2 gap-6">
          <div
            v-for="(product, index) in displayedItems" 
            :key="index"
            ref="productCard"
            class="product-card relative overflow-hidden grid grid-rows-[auto,1fr]"
          >
            <!-- 分享按钮 -->
            <button
              @click="openShareModal(index)"
              class="absolute top-2 right-2 border px-3 py-1 rounded-sm transition-opacity duration-300 card-hover-btn"
            >
              <i class="fas fa-share-alt"></i>
            </button>
  
            <!-- name/amount 布局 -->
            <div class="flex items-center justify-start px-6 pt-5">
              <div>
                <a :href="product.website_url" target="_blank" class="funding-name text-xl font-semibold transition-colors">
                  {{ product.name }}
                </a>
                <p v-if="product.round" class="text-sm text-gray-500 mt-3">
                    <span>{{ $t('interface.funding.round') }}</span><span class="amount-number whitespace-nowrap ml-2">{{ product.round }}</span>
                </p>
                <p v-if="product.amount" class="text-sm text-gray-500 mt-3">
                    <span>{{ $t('interface.funding.funding_amount') }}  </span><span class="amount-number whitespace-nowrap ml-2">{{ formatAmount(product.amount) }}</span>
                </p>
                <p v-if="product.valuation" class="text-sm text-gray-500 mt-3">
                    <span>{{ $t('interface.funding.valuation') }}  </span><span class="amount-number whitespace-nowrap ml-2">{{ formatAmount(product.valuation) }}</span>
                </p>
              </div>
            </div>
  
            <!-- 内容部分 -->
            <div class="px-6 py-4">
              <div class="text-gray-800 font-medium mb-2 chinese-description">
                <p>{{ product[`intro_${$i18n.locale}`] || product.中文简介 }}</p>
              </div>
              <div class="text-gray-600 text-sm leading-relaxed purpose-description">
                <p v-for="(part, index) in highlightedPurpose(product[`usage_${$i18n.locale}`] || product.用途说明)" :key="index" v-html="part"></p>
              </div>
              <!-- lead_investor 信息展示 -->
              <div v-if="product.lead_investor" class="text-sm text-gray-500 mt-3">
                <span>{{ product.lead_investor }}</span><span class="ml-2">{{ $t('interface.funding.lead_investor') }}</span>
              </div>
              <!-- 收录时间 record_date展示 -->
              <!-- <div v-if="product.record_date" class="text-[11px] text-gray-500 mt-3 mb-0 text-right">
                <span>{{ product.record_date }}</span><span> 收录</span>
              </div> -->
            </div>
  
            <!-- 图片展示区域 -->
            <div v-if="product.image_url" class="w-full h-full overflow-hidden">
              <img :src="product.image_url" :alt="$t('interface.image')" class="w-full h-full object-cover" />
            </div>
          </div>
        </div>
      </transition>

        <!-- "显示更多"按钮 -->
        <button
        v-if="hasMoreItems"
        @click="loadMore"
        class="show-more-button mt-6 mx-auto block flex items-center space-x-2 px-6 py-1 transition-colors duration-200">
        <i class="fas fa-angle-double-down"></i>
        <span>{{ $t('interface.show_more') }}</span>
      </button>
  
      <!-- 分享弹窗（独立组件，承载模板/样式/校验） -->
      <ShareModal :visible="showModal" :is-generating="isGenerating" @submit="onShareSubmit" @close="closeModal" />
    </div>
  </template>
  
  <script>
  import ShareModal from './ShareModal.vue'; // 分享弹窗（公共组件）
  import gridPaginationMixin from '../mixins/gridPaginationMixin'; // 分页状态机
  import transitionMixin from '../mixins/transitionMixin'; // 淡入过渡钩子
  import gridShareMixin from '../mixins/gridShareMixin'; // 分享逻辑（showModal/isGenerating/onShareSubmit 等）
import { splitPurposeByLine } from '../utils/purpose'; // 用途说明按换行拆分（Funding/Channel 共用）

  export default {
    components: {
      ShareModal,
    },
    mixins: [gridPaginationMixin, transitionMixin, gridShareMixin],
    data() {
      return {
        itemsPerPage: 6, // 每页显示的条目数
        sourceKey: 'products',
        paginationGtagLabel: 'funding',
        // 分享图配置（覆盖 gridShareMixin 默认）
        shareCardWidth: 450,
        shareGtagType: 'funding',
      };
    },
    props: {
        products: {
            type: Array,
            default: () => [] // 默认值为空数组，避免 undefined 错误
        }
    },
    methods: {
        formatAmount(amount) {
            if (typeof amount === 'string') {
                if (amount.includes('$')) {
                    // 处理类似 "$1.3B" 或 "$5M" 的格式
                    const numStr = amount.replace(/[^0-9.]/g, '');
                    const num = parseFloat(numStr);
                    
                    if (amount.includes('B')) {
                        // 处理十亿单位
                        return `$ ${num.toFixed(1)} B`;
                    } else if (amount.includes('M')) {
                        // 处理百万单位
                        return `$ ${num.toFixed(1)} M`;
                    }
                }
                // 如果无法识别格式，返回原值
                return amount;
            }
            // 处理纯数字，默认转为百万单位
            const amountInMillions = amount / 1000000;
            return `$ ${amountInMillions.toFixed(1)} M`;
        },
        // 用途说明：仅按换行拆分+trim（无关键词高亮，与 ChannelGrid 共用 splitPurposeByLine）
        highlightedPurpose(purpose) {
            return splitPurposeByLine(purpose);
        },
      },
  };
</script>
  
  <style scoped>
  /* 以下为 Funding 专属样式；卡片容器/标签/简介/分享按钮/淡入等共用样式已提升到
     src/styles/cards.css（全局）。 */

  /* 产品名 —— 墨黑，hover 转黄铜 */
.funding-name {
  color: var(--ink, #1b1815);
}

.funding-name:hover {
  color: var(--brass, #b39a6b);
}

video {
  object-fit: cover;
}

.amount-number {
  color: var(--brass, #b39a6b);
  font-weight: 700;
  font-size: 14px;
}
  </style>
